import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import useSWR from 'swr';
import Badge from '../../components/ui/Badge';
import {
  Button,
  GeneralList,
  GeneralListSortableTh,
  PrinterIcon,
  RefreshIcon,
  SearchIcon,
  UserIcon,
  VesselSelect,
  useGeneralListPageSize,
  useGeneralListSort,
} from '../../components/ui';
import { formatDate } from '../../config/appConfig';
import { useTheme } from '../../contexts/ThemeContext';
import { cn } from '../../lib/utils';
import { fetcher } from '../../services/fetcher';
import { CrewHelpHint } from './CrewReportSearchHeader';
import { CrewReportViewAction, useCrewReportOpen } from './reportShared';
import { certGapsPrintPath } from './reports/certGapsPrintMeta';
import type { CertGapResponse, CertGapRow } from './types';

type StatusTab = 'gaps' | 'ok' | 'all';

const display = (value: string | number | null | undefined) =>
  value == null || value === '' ? '—' : String(value);

function sortValue(row: CertGapRow, key: string): string | number {
  switch (key) {
    case 'name':
      return (row.name || '').toLowerCase();
    case 'rank':
      return (row.rank || '').toLowerCase();
    case 'nationality':
      return (row.nationality || '').toLowerCase();
    case 'certificate':
      return (row.certificate || '').toLowerCase();
    case 'status':
      return row.status;
    case 'expiredDate':
      return row.expiredDate || '';
    default:
      return row.crewId;
  }
}

const CertGapsPage: React.FC = () => {
  const { colors } = useTheme();
  const { listReturnPath, openCrew } = useCrewReportOpen();

  const [vesselId, setVesselId] = useState('');
  const [appliedVesselId, setAppliedVesselId] = useState('');
  const [tab, setTab] = useState<StatusTab>('gaps');
  const [page, setPage] = useState(1);
  const { itemsPerPage, setItemsPerPage, pageSizeOptions } = useGeneralListPageSize();
  const { sortConfig, handleSort } = useGeneralListSort({ key: 'name', direction: 'asc' });

  const listUrl = appliedVesselId
    ? `/crew/reports/cert-gaps?vesselId=${encodeURIComponent(appliedVesselId)}`
    : null;
  const { data, error, isLoading, mutate } = useSWR<CertGapResponse>(listUrl, fetcher, {
    revalidateOnFocus: false,
  });

  const filteredRows = useMemo(() => {
    const list = data?.rows ?? [];
    if (tab === 'gaps') return list.filter((r) => r.status !== 'OK');
    if (tab === 'ok') return list.filter((r) => r.status === 'OK');
    return list;
  }, [data?.rows, tab]);

  const sortedRows = useMemo(() => {
    const rows = [...filteredRows];
    const dir = sortConfig.direction === 'asc' ? 1 : -1;
    rows.sort((a, b) => {
      const av = sortValue(a, sortConfig.key);
      const bv = sortValue(b, sortConfig.key);
      if (av < bv) return -1 * dir;
      if (av > bv) return 1 * dir;
      return (a.crewId - b.crewId) * dir || a.certCatalogId - b.certCatalogId;
    });
    return rows;
  }, [filteredRows, sortConfig.key, sortConfig.direction]);

  const total = sortedRows.length;
  const totalPages = Math.max(1, Math.ceil(total / itemsPerPage));
  const pageRows = useMemo(() => {
    const start = (page - 1) * itemsPerPage;
    return sortedRows.slice(start, start + itemsPerPage);
  }, [sortedRows, page, itemsPerPage]);

  useEffect(() => {
    setPage(1);
  }, [tab, appliedVesselId, itemsPerPage, sortConfig.key, sortConfig.direction]);

  useEffect(() => {
    if (page > totalPages) setPage(totalPages);
  }, [page, totalPages]);

  const handleSearch = useCallback(() => {
    if (!vesselId) return;
    setAppliedVesselId(vesselId);
    setPage(1);
  }, [vesselId]);

  const clearFilters = () => {
    setVesselId('');
    setAppliedVesselId('');
    setTab('gaps');
    setPage(1);
  };

  const hasFilters = Boolean(vesselId || appliedVesselId || tab !== 'gaps');

  const contextLabel =
    data?.vessel && appliedVesselId
      ? `${data.vessel.vesselName}${data.vessel.vesselAbrv ? ` (${data.vessel.vesselAbrv})` : ''} · ${data.summary.gapCount} gaps · ${data.summary.okCount} ok · ${data.summary.crewCount} crew`
      : null;

  return (
    <GeneralList
      headerStart={
        <CrewHelpHint
          hint={{
            title: 'Document Gaps vs requirements',
            body: 'Compares onboard crew certificates against rank requirements for the selected vessel (vessel-specific plus legacy all-vessel rules). Shows missing or expired certificates by nationality.',
          }}
          className="self-center"
        />
      }
      tabs={[
        { id: 'gaps', label: 'Gaps only' },
        { id: 'ok', label: 'OK only' },
        { id: 'all', label: 'All' },
      ]}
      activeTab={tab}
      onTabChange={(value) => setTab(value as StatusTab)}
      sorting={{ sortConfig, onSort: handleSort }}
      filtersInline
      filters={
        <div className="flex flex-wrap items-center gap-2">
          <div className="w-[14rem] max-w-full">
            <VesselSelect
              name="vesselId"
              value={vesselId}
              onChange={(e) => setVesselId(String(e.target.value))}
              placeholder="Vessel"
              includeIds={vesselId ? [vesselId] : undefined}
              triggerClassName="!h-10 !min-h-10 !py-0 box-border"
            />
          </div>
          {contextLabel ? (
            <span className={cn('text-xs whitespace-nowrap', colors.textMuted)}>{contextLabel}</span>
          ) : null}
        </div>
      }
      hasFilters={hasFilters}
      onClearFilters={clearFilters}
      actions={
        <div className="flex h-10 items-center gap-2">
          <Button
            type="button"
            className="h-10"
            disabled={!vesselId || isLoading}
            onClick={handleSearch}
          >
            <SearchIcon className="h-4 w-4" />
            Search
          </Button>
          {appliedVesselId ? (
            <>
              <Button
                type="button"
                variant="outline"
                className="h-10"
                disabled={isLoading}
                onClick={() => void mutate()}
                title="Refresh"
              >
                <RefreshIcon className="h-4 w-4" />
                Refresh
              </Button>
              <Link
                to={certGapsPrintPath({ vesselId: appliedVesselId, tab })}
                title="Print preview"
                className={cn(
                  'inline-flex h-10 items-center gap-2 rounded-md border px-3 text-sm font-medium transition-colors',
                  colors.border,
                  colors.text,
                  'hover:bg-black/5 dark:hover:bg-white/10',
                )}
              >
                <PrinterIcon className="h-4 w-4" />
                Print
              </Link>
            </>
          ) : null}
        </div>
      }
      isLoading={Boolean(appliedVesselId) && isLoading}
      loadingLabel="Loading document gaps…"
      isEmpty={!error && Boolean(appliedVesselId) && pageRows.length === 0 && !isLoading}
      emptyState={{
        icon: <UserIcon className="h-12 w-12" />,
        title: tab === 'gaps' ? 'No document gaps' : 'No matching rows',
        hint:
          tab === 'gaps'
            ? 'All required certificates are present and valid for this vessel.'
            : 'Try another status tab or clear filters.',
        action: hasFilters ? (
          <button
            type="button"
            onClick={clearFilters}
            className={cn(
              'inline-flex items-center gap-1.5 rounded-xl border px-4 py-2.5 text-sm font-medium',
              colors.border,
              colors.text,
            )}
          >
            Clear filters
          </button>
        ) : undefined,
      }}
      banner={
        error ? (
          <p className={cn('rounded-xl border px-3 py-2 text-sm', colors.error)}>
            Could not load gaps.
          </p>
        ) : null
      }
      pagination={
        appliedVesselId
          ? {
              page,
              totalPages,
              total,
              itemsPerPage,
              pageSizeOptions,
              onPageChange: setPage,
              onItemsPerPageChange: (size) => {
                setItemsPerPage(size);
                setPage(1);
              },
            }
          : undefined
      }
    >
      {appliedVesselId && !error && pageRows.length > 0 ? (
        <table className="w-full min-w-[900px] text-sm">
          <thead>
            <tr className={cn('border-b text-left text-xs uppercase tracking-wide', colors.border, colors.textMuted)}>
              <th className={cn('sticky top-0 z-10 w-12 font-medium', colors.surface)} />
              <GeneralListSortableTh sortKey="name" className="whitespace-nowrap">
                Crew
              </GeneralListSortableTh>
              <GeneralListSortableTh sortKey="rank" className="whitespace-nowrap">
                Rank
              </GeneralListSortableTh>
              <GeneralListSortableTh sortKey="nationality" className="whitespace-nowrap">
                Nationality
              </GeneralListSortableTh>
              <GeneralListSortableTh sortKey="certificate" className="whitespace-nowrap">
                Required certificate
              </GeneralListSortableTh>
              <GeneralListSortableTh sortKey="status" className="whitespace-nowrap">
                Status
              </GeneralListSortableTh>
              <GeneralListSortableTh sortKey="expiredDate" className="whitespace-nowrap">
                Expiry
              </GeneralListSortableTh>
            </tr>
          </thead>
          <tbody>
            {pageRows.map((row) => (
              <tr
                key={`${row.crewId}-${row.certCatalogId}`}
                onClick={() => openCrew(row.crewId)}
                className={cn(
                  'group cursor-pointer border-b transition last:border-b-0',
                  colors.border,
                  colors.interactiveHover,
                )}
              >
                <td onClick={(e) => e.stopPropagation()}>
                  <CrewReportViewAction crewId={row.crewId} from={listReturnPath} />
                </td>
                <td>
                  <Link
                    to={`/crew/${row.crewId}`}
                    state={{ from: listReturnPath }}
                    className={cn('text-sm font-medium hover:underline', colors.primaryFg)}
                    onClick={(e) => e.stopPropagation()}
                  >
                    {row.name}
                  </Link>
                </td>
                <td className={cn('whitespace-nowrap', colors.text)}>{display(row.rank)}</td>
                <td className={cn('whitespace-nowrap', colors.text)}>{display(row.nationality)}</td>
                <td className={cn('whitespace-nowrap', colors.text)}>{display(row.certificate)}</td>
                <td>
                  <Badge
                    variant={row.status === 'OK' ? 'success' : row.status === 'EXPIRED' ? 'warning' : 'danger'}
                    className="rounded-md px-2 py-0.5"
                  >
                    {row.status}
                  </Badge>
                </td>
                <td className={cn('whitespace-nowrap', colors.textMuted)}>
                  {row.expiredDate ? formatDate(row.expiredDate) : '—'}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      ) : null}
    </GeneralList>
  );
};

export default CertGapsPage;
